import { UserWithCustomerResource, ViewUserResource, usePatchProfilePictureMutation } from '@/api/serverApi'
import { ImageInput, UserAvatar, useVaporImageUpload } from '@cfs-fixings/shared'
import toastHelper from '@cfs-fixings/shared/src/helpers/toastHelper'
import { useState } from 'react'

type PropTypes = {
  user: UserWithCustomerResource | ViewUserResource
}

const textSpec = {
  title: 'Profile Picture',
  successToastMessage: {
    title: 'Profile picture changed successfully',
    body: 'Your profile picture has now been changed.',
  },
  invalidFileTypeToastMessage: {
    title: 'Invalid file type',
    body: 'Please upload a valid image file.',
  },
  errorToastMessage: {
    title: 'Something went wrong',
    body: 'Please try again later.',
  },
}

function EditProfilePictureForm ({ user }: PropTypes) {
  const [profilePictureOverride, setProfilePictureOverride] = useState<string | undefined>(undefined)
  const [isSettingProfilePicture, setIsSettingProfilePicture] = useState<boolean>(false)

  const [update] = usePatchProfilePictureMutation()

  const { uploadImage } = useVaporImageUpload({
    baseUrl: import.meta.env.VITE_API_BASE_URL,
  })

  return (
    <div className="flex flex-col items-start w-full">

      <h2 className="mb-[25px] text-lg leading-[21.6px] font-semibold">{textSpec.title}</h2>

      <div className="w-full flex flex-row justify-between items-end">
        <ImageInput
          formElement={(ref) => {
            return (
              <UserAvatar
                user={user}
                onClick={() => ref.current?.click()}
                isLoading={isSettingProfilePicture}
                profilePictureOverride={profilePictureOverride}
                size="large"
              />
            )
          }}
          onImageChange={(file) => {
            setIsSettingProfilePicture(true)

            const reader = new FileReader()

            reader.onload = (event) => {
              // Set a temporary profile picture from the file reader so we can
              // show an optimistic successful image change
              setProfilePictureOverride(event.target?.result as string)
            }
            reader.readAsDataURL(file)

            // Upload to S3
            uploadImage(file).then((response) => {
              // Actually update the profile picture linked to the user record
              update({
                body: {
                  profile_picture: {
                    key: response.key,
                    uuid: response.uuid,
                  },
                },
              })
                .unwrap()
                .then(() => {
                  toastHelper.success(textSpec.successToastMessage.title, textSpec.successToastMessage.body)
                }).catch((error) => {
                  if (error.status === 422) {
                    toastHelper.error(textSpec.invalidFileTypeToastMessage.title, textSpec.invalidFileTypeToastMessage.body)
                  } else {
                    toastHelper.error(textSpec.errorToastMessage.title, textSpec.errorToastMessage.body)
                  }
                }).finally(() => {
                  setIsSettingProfilePicture(false)
                })
            }).catch(() => {
              toastHelper.error(textSpec.errorToastMessage.title, textSpec.errorToastMessage.body)
            }).finally(() => {
              setIsSettingProfilePicture(false)
            })
          }}
        />
      </div>

    </div>
  )
}

export default EditProfilePictureForm
